import { useState, useEffect } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchSections(instructorId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections?instructor_id=eq.${instructorId}&select=*&order=section_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function createSection(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Creating syllabus section failed (${response.status}): ${detail}`);
  }

  return response.json();
}

async function fetchQuizQuestions(sectionId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_quiz_questions?syllabus_section_id=eq.${sectionId}&select=*&order=question_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function updateSection(id, instructorId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections?id=eq.${id}&instructor_id=eq.${instructorId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function saveQuizQuestion(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_quiz_questions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function deleteQuizQuestion(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_quiz_questions?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
}

const VARIABLES = [
  { label: 'Course name', placeholder: '{{course_name}}' },
  { label: 'Semester', placeholder: '{{semester}}' },
  { label: 'Class day', placeholder: '{{class_day}}' },
  { label: 'Class time', placeholder: '{{class_time}}' },
  { label: 'PD due day', placeholder: '{{pd_due_day}}' },
  { label: 'Semester start date', placeholder: '{{start_date}}' },
  { label: 'Mid-Semester Self-Evaluation due date', placeholder: '{{mid_eval_close}}' },
{ label: 'Final Self-Evaluation due date', placeholder: '{{final_eval_close}}' },
{ label: 'Clarificatory questions allowed', placeholder: '{{cq_clarificatory_allowed}}' },
];

export default function GeneralSyllabus({ session }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [quizQuestions, setQuizQuestions] = useState({});
  const [quizLoading, setQuizLoading] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newQuiz, setNewQuiz] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    fetchSections(session.instructorId)
  .then(data => {
    const rows = Array.isArray(data) ? data : [];

    setSections(
      rows.filter(
        section =>
          String(section.name || '').trim().toLowerCase() !==
          'other assignments'
      )
    );
  })
  .catch(() => setError('Failed to load syllabus sections.'))
  .finally(() => setLoading(false));
  }, [session.instructorId]);

  async function handleCreateSection(e) {
    e.preventDefault();

    const name = newSectionName.trim();

    if (!name) {
      setError('Enter a section name.');
      return;
    }

    if (
      sections.some(
        section =>
          String(section.name || '').trim().toLowerCase() ===
          name.toLowerCase()
      )
    ) {
      setError('You already have a syllabus section with that name.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const maxOrder = sections.reduce(
        (max, section) =>
          Math.max(max, Number(section.section_order) || 0),
        0
      );

      const result = await createSection({
        name,
        section_order: maxOrder + 1,
        cooldown_minutes: 0,
        has_quiz: true,
        is_variable: true,
        is_teaching_schedule: false,
        is_ai_permissions: false,
        has_clarificatory_variants: false,
        instructor_id: session.instructorId,
        content: ''
      });

      const created = Array.isArray(result) ? result[0] : null;

      if (!created) {
        throw new Error('No syllabus section was returned after creation.');
      }

      setSections(prev =>
        [...prev, created].sort(
          (a, b) =>
            (Number(a.section_order) || 0) -
            (Number(b.section_order) || 0)
        )
      );

      setEditValues(prev => ({
        ...prev,
        [created.id]: {
          name: created.name || '',
          content: created.content || '',
          content_with_clarificatory:
            created.content_with_clarificatory || '',
          content_without_clarificatory:
            created.content_without_clarificatory || '',
          cooldown_minutes: created.cooldown_minutes ?? 0,
          has_quiz: created.has_quiz
        }
      }));

      setExpandedId(created.id);
      setNewSectionName('');
      setShowAddSection(false);
      setSuccess('Section created. You can edit it below.');
    } catch (err) {
      console.error('Syllabus section creation failed:', err);
      setError(err?.message || 'Failed to create syllabus section.');
    } finally {
      setSaving(false);
    }
  }

  async function handleExpand(section) {
    if (expandedId === section.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(section.id);
    setEditValues(prev => ({
      ...prev,
      [section.id]: {
        name: section.name || '',
        content: section.content || '',
        content_with_clarificatory: section.content_with_clarificatory || '',
        content_without_clarificatory: section.content_without_clarificatory || '',
        cooldown_minutes: section.cooldown_minutes ?? 0,
        has_quiz: section.has_quiz
      }
    }));

    if (section.has_quiz && !quizQuestions[section.id]) {
      setQuizLoading(prev => ({ ...prev, [section.id]: true }));
      const questions = await fetchQuizQuestions(section.id);
      setQuizQuestions(prev => ({ ...prev, [section.id]: questions }));
      setQuizLoading(prev => ({ ...prev, [section.id]: false }));
    }
    setError('');
    setSuccess('');
  }

  async function handleSaveSection(section) {
    setSaving(true);
    setError('');
    try {
      const values = editValues[section.id];
      const updateData = {
        name: values.name ?? section.name,
        cooldown_minutes: Number(values.cooldown_minutes),
        updated_at: new Date().toISOString()
      };

      if (section.has_clarificatory_variants) {
        updateData.content_with_clarificatory = values.content_with_clarificatory;
        updateData.content_without_clarificatory = values.content_without_clarificatory;
      } else if (!section.is_teaching_schedule) {
        updateData.content = values.content;
      }

      const result = await updateSection(
        section.id,
        session.instructorId,
        updateData
      );
if (Array.isArray(result) && result[0]) {
  setSections(prev =>
    prev.map(s =>
      s.id === section.id ? result[0] : s
    )
  );
}

setExpandedId(null);
setSuccess('Section saved.');
    } catch (err) {
      setError('Failed to save section.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddQuizQuestion(sectionId) {
  const q = newQuiz[sectionId] || {};
  if (!q.question?.trim()) return setError('Enter a question.');
  const choices = (q.choices || '').split('\n').map(c => c.trim()).filter(Boolean);
  if (choices.length < 2) return setError('Enter at least two choices.');
  if (q.correct_answer_index === undefined || q.correct_answer_index === '') {
    return setError('Select the correct answer.');
  }

  setSaving(true);
  try {
    const existing = quizQuestions[sectionId] || [];
    const result = await saveQuizQuestion({
      syllabus_section_id: sectionId,
      question: q.question.trim(),
      choices: JSON.stringify(choices),
      correct_answer_index: Number(q.correct_answer_index),
      correct_answer: choices[Number(q.correct_answer_index)],
      question_order: existing.length + 1
    });
    setQuizQuestions(prev => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), result[0]]
    }));
    setNewQuiz(prev => ({ ...prev, [sectionId]: { question: '', choices: '', correct_answer_index: '' } }));
    setSuccess('Quiz question added.');
  } catch (err) {
    setError('Failed to add quiz question.');
  } finally {
    setSaving(false);
  }
}

  async function handleDeleteQuizQuestion(sectionId, questionId) {
    await deleteQuizQuestion(questionId);
    setQuizQuestions(prev => ({
      ...prev,
      [sectionId]: prev[sectionId].filter(q => q.id !== questionId)
    }));
    setSuccess('Quiz question deleted.');
  }

  function insertVariable(sectionId, placeholder, variantKey) {
    setEditValues(prev => {
      const current = prev[sectionId];
      if (variantKey) {
        return { ...prev, [sectionId]: { ...current, [variantKey]: (current[variantKey] || '') + placeholder } };
      }
      return { ...prev, [sectionId]: { ...current, content: (current.content || '') + placeholder } };
    });
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Syllabus Sections</h2>
      <p style={styles.muted}>
        Edit your syllabus section templates. Variable sections
        (This Course, Teaching Schedule, assignment sections) are edited per course.
        Use the variable picker to insert course-specific data into templates.
      </p>

      <div style={styles.addSectionBar}>
        {!showAddSection ? (
          <button
            type="button"
            style={styles.btn}
            onClick={() => {
              setShowAddSection(true);
              setError('');
              setSuccess('');
            }}
          >
            Add Syllabus Section
          </button>
        ) : (
          <form onSubmit={handleCreateSection} style={styles.addSectionForm}>
            <div style={styles.addSectionField}>
              <label style={styles.label}>Section name</label>
              <input
                style={styles.input}
                type="text"
                value={newSectionName}
                onChange={e => setNewSectionName(e.target.value)}
                placeholder="e.g. Discussion Expectations"
                autoFocus
              />
            </div>
            <div style={styles.addSectionButtons}>
              <button
                type="submit"
                style={styles.btn}
                disabled={saving}
              >
                {saving ? 'Creating...' : 'Create Section'}
              </button>
              <button
                type="button"
                style={styles.secondaryBtn}
                disabled={saving}
                onClick={() => {
                  setShowAddSection(false);
                  setNewSectionName('');
                  setError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.list}>
        {sections.length === 0 && !error && (
          <div style={styles.muted}>
            No syllabus sections yet. Use “Add Syllabus Section” above to create your first one.
          </div>
        )}
        {sections.map((section, index) => {
          const isExpanded = expandedId === section.id;
          const values = editValues[section.id] || {};
          const isVariable = section.is_variable;
          const isTeachingSchedule = section.is_teaching_schedule;
          const isClarificatory = section.has_clarificatory_variants;
          const questions = quizQuestions[section.id] || [];
          const nq = newQuiz[section.id] || {};

          return (
            <div key={section.id} style={styles.sectionItem}>
              <button
                style={styles.sectionBtn}
                onClick={() => handleExpand(section)}
              >
                <div style={styles.sectionBtnLeft}>
                  <span style={styles.sectionOrder}>{index + 1}</span>
                  <span style={styles.sectionName}>{section.name}</span>
                  <div style={styles.pills}>
                    {isVariable && <span style={styles.pill}>Per course</span>}
                    {isTeachingSchedule && <span style={styles.pillAlt}>Teaching Schedule</span>}
                    {isClarificatory && <span style={styles.pillAlt}>Two versions</span>}
                    {section.has_quiz && <span style={styles.pillQuiz}>Has quiz</span>}
                    <span style={styles.pillCooldown}>{section.cooldown_minutes}min cooldown</span>
                  </div>
                </div>
                <span style={styles.chevron}>{isExpanded ? '▾' : '▸'}</span>
              </button>

              {isExpanded && (
                <div style={styles.sectionContent}>
                  {/* Name */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={styles.label}>Section name</label>
                    <input
                      style={styles.cooldownInput}
                      type="text"
                      value={values.name ?? section.name}
                      onChange={e => setEditValues(prev => ({
                        ...prev,
                        [section.id]: { ...prev[section.id], name: e.target.value }
                      }))}
                    />
                  </div>
                  {/* Cooldown */}
                  <div style={styles.cooldownRow}>
                    <label style={styles.label}>Cooldown (minutes)</label>
                    <input
                      style={styles.cooldownInput}
                      type="number"
                      min="0"
                      value={values.cooldown_minutes ?? 0}
                      onChange={e => setEditValues(prev => ({
                        ...prev,
                        [section.id]: { ...prev[section.id], cooldown_minutes: e.target.value }
                      }))}
                    />
                  </div>

                  {/* Content */}
                  {!isVariable && !isTeachingSchedule && (
                    <>
                      {isClarificatory ? (
                        <div>
                          <div style={styles.variantLabel}>
                            Version when clarificatory questions are enabled:
                          </div>
                          <VariablePicker
                            onInsert={p => insertVariable(section.id, p, 'content_with_clarificatory')}
                          />
                          <RichTextEditor
                            value={values.content_with_clarificatory || ''}
                            onChange={val => setEditValues(prev => ({
                              ...prev,
                              [section.id]: { ...prev[section.id], content_with_clarificatory: val }
                            }))}
                          />
                          <div style={{ ...styles.variantLabel, marginTop: '16px' }}>
                            Version when clarificatory questions are not enabled:
                          </div>
                          <VariablePicker
                            onInsert={p => insertVariable(section.id, p, 'content_without_clarificatory')}
                          />
                          <RichTextEditor
                            value={values.content_without_clarificatory || ''}
                            onChange={val => setEditValues(prev => ({
                              ...prev,
                              [section.id]: { ...prev[section.id], content_without_clarificatory: val }
                            }))}
                          />
                        </div>
                      ) : (
                        <>
                          <VariablePicker
                            onInsert={p => insertVariable(section.id, p, null)}
                          />
                          <RichTextEditor
                            value={values.content || ''}
                            onChange={val => setEditValues(prev => ({
                              ...prev,
                              [section.id]: { ...prev[section.id], content: val }
                            }))}
                          />
                        </>
                      )}
                    </>
                  )}

                  {isTeachingSchedule && (
  <div style={styles.muted}>
    Teaching Schedule content is configured per course in Course Management.
  </div>
)}
{isVariable && !isTeachingSchedule && (
  <>
    <div style={styles.muted}>
      This content serves as a default template. It can be overridden per course in Course Management.
    </div>
    <VariablePicker
      onInsert={p => insertVariable(section.id, p, null)}
    />
    <RichTextEditor
      value={values.content || ''}
      onChange={val => setEditValues(prev => ({
        ...prev,
        [section.id]: { ...prev[section.id], content: val }
      }))}
    />
  </>
)}
                  <button
                    style={{ ...styles.btn, marginTop: '14px' }}
                    disabled={saving}
                    onClick={() => handleSaveSection(section)}
                  >
                    {saving ? 'Saving...' : 'Save Section'}
                  </button>
                  {section.is_ai_permissions && (
  <div style={{ ...styles.muted, marginTop: '12px' }}>
    The list of permitted and not permitted AI use types is auto-generated from each course's AI Permissions settings and will appear below this text in the student view.
  </div>
)}

                  {/* Quiz questions */}
                  {section.has_quiz && (
                    <div style={styles.quizSection}>
                      <h4 style={styles.quizHeading}>Quiz Questions</h4>
                      {quizLoading[section.id] && <div style={styles.muted}>Loading...</div>}

                      {questions.map(q => {
  const choices = (() => { try { return JSON.parse(q.choices || '[]'); } catch { return []; } })();
  return (
    <div key={q.id} style={styles.quizItem}>
      <div style={styles.quizQuestion}>{q.question}</div>
      <div style={styles.quizChoices}>
        {choices.map((c, i) => (
          <div key={i} style={{
            ...styles.quizChoice,
            ...(i === q.correct_answer_index ? styles.quizChoiceCorrect : {})
          }}>
            {i === q.correct_answer_index ? '✓ ' : '○ '}{c}
          </div>
        ))}
      </div>
      <button
        style={styles.dangerBtn}
        onClick={() => handleDeleteQuizQuestion(section.id, q.id)}
      >
        Delete
      </button>
    </div>
  );
})}

                      <div style={styles.addQuizForm}>
  <div style={styles.field}>
    <label style={styles.label}>Question</label>
    <input
      style={styles.input}
      type="text"
      value={nq.question || ''}
      onChange={e => setNewQuiz(prev => ({
        ...prev,
        [section.id]: { ...prev[section.id], question: e.target.value }
      }))}
      placeholder="Enter quiz question..."
    />
  </div>
  <div style={styles.field}>
    <label style={styles.label}>Answer choices (one per line)</label>
    <textarea
      style={styles.textarea}
      value={nq.choices || ''}
      onChange={e => setNewQuiz(prev => ({
        ...prev,
        [section.id]: { ...prev[section.id], choices: e.target.value }
      }))}
      rows={4}
      placeholder={'Yes\nNo\nSometimes\nNever'}
    />
  </div>
  <div style={styles.field}>
    <label style={styles.label}>Correct answer</label>
    <select
      style={styles.input}
      value={nq.correct_answer_index ?? ''}
      onChange={e => setNewQuiz(prev => ({
        ...prev,
        [section.id]: { ...prev[section.id], correct_answer_index: Number(e.target.value) }
      }))}
    >
      <option value="">Select correct answer...</option>
      {(nq.choices || '').split('\n').map((c, i) => c.trim()).filter(Boolean).map((c, i) => (
        <option key={i} value={i}>{c}</option>
      ))}
    </select>
  </div>
  <button
    style={styles.secondaryBtn}
    disabled={saving}
    onClick={() => handleAddQuizQuestion(section.id)}
  >
    Add Quiz Question
  </button>
</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VariablePicker({ onInsert }) {
  const [selected, setSelected] = useState('');
  return (
    <div style={styles.variablePicker}>
      <label style={styles.varLabel}>Insert variable:</label>
      <div style={styles.varRow}>
        <select
          style={styles.varSelect}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">Select a variable</option>
          {VARIABLES.map(v => (
            <option key={v.placeholder} value={v.placeholder}>{v.label}</option>
          ))}
        </select>
        <button
          type="button"
          style={styles.varInsertBtn}
          disabled={!selected}
          onClick={() => { onInsert(selected); setSelected(''); }}
        >
          Insert
        </button>
      </div>
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 12px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  addSectionBar: {
    marginBottom: '18px'
  },
  addSectionForm: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    background: '#f7f9fa',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    flexWrap: 'wrap'
  },
  addSectionField: {
    flex: 1,
    minWidth: '240px'
  },
  addSectionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionItem: {
    border: '1px solid #d7dee5', borderRadius: '10px', overflow: 'hidden', background: 'white'
  },
  sectionBtn: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left'
  },
  sectionBtnLeft: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  sectionOrder: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '50%',
    width: '24px', height: '24px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0
  },
  sectionName: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillAlt: {
    background: '#fff4d7', color: '#8a5b00', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillQuiz: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillCooldown: {
    background: '#f0f4f7', color: '#66788a', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  chevron: { color: '#66788a', fontSize: '16px', flexShrink: 0 },
  sectionContent: { padding: '16px', borderTop: '1px solid #d7dee5' },
  cooldownRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  cooldownInput: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '7px 10px',
    fontSize: '14px', width: '80px', background: 'white'
  },
  variantLabel: { fontSize: '13px', fontWeight: '700', color: '#1f2933', marginBottom: '8px' },
  variablePicker: {
    background: '#f7f9fa', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 12px', marginBottom: '10px'
  },
  varLabel: { fontSize: '12px', fontWeight: '700', color: '#66788a', marginBottom: '6px', display: 'block' },
  varRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  varSelect: {
    flex: 1, border: '1px solid #d7dee5', borderRadius: '6px',
    padding: '6px 10px', fontSize: '13px', background: 'white'
  },
  varInsertBtn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '6px',
    padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer'
  },
  quizSection: {
    marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #d7dee5'
  },
  quizHeading: { margin: '0 0 12px', fontSize: '14px', color: '#1f2933', fontWeight: '700' },
  quizItem: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '10px 12px',
    marginBottom: '8px', background: '#f7f9fa', display: 'flex',
    alignItems: 'flex-start', gap: '12px'
  },
  quizQuestion: { flex: 1, fontSize: '14px', color: '#1f2933', fontWeight: '600' },
  quizAnswer: { fontSize: '13px', color: '#216e45', flexShrink: 0 },
  addQuizForm: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '14px',
    background: 'white', marginTop: '10px'
  },
  field: { marginBottom: '12px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  }
};